import React, { memo, useCallback, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';

import { Text, View } from '../Base';
import { CloseIcon } from '../icons';

import palette from '../styles/palette';
import { getStopRequestType } from '../../features/routeDetail/model';
import { getRequestType } from '../../features/request/model';
import { requestIconType, DRIVER_REQUEST_TYPE } from '../../enums/requestTypes';
import {
  ADD_COLLECTION,
  ADD_PARCEL,
  CONTAINS_MULTIPLE_REQUESTS,
  DEFERRED_REQUEST,
  EXPAND,
  MIXED_REQUEST,
  PICKUP_UNSUITABLE,
  REMOVE_COLLECTION,
  REMOVE_PARCEL,
  REQUEST,
  UNABLE_PROCEED_PICKUP_REQUEST,
} from '../../constants/strings';
import Button from '../Button';
import GoToRouteDetailsWarning from './GoToRouteDetailsWarning';
import CollectionRequest from './CollectionRequest';
import PickupRequest from './PickupRequest';
import ParcelRequest from './ParcelRequest';
import modals from '../../enums/modals';
import { wrapPromise } from '../../utils/promise';
import stopTypes from '../../enums/stopTypes';
import PopupRequestActions from './PopupRequestActions';
import { useModalContext } from '../ModalControlled/ModalProvider';

const useStyles = makeStyles({
  popupContainer: {
    borderRadius: '8px',
    width: '260px',
    minHeight: '125px',
    padding: '10px',
    background: palette.typography.black.high,
    fontFamily: 'Roboto',
  },
  popupHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: '18px',
    fontWeight: '500',
    lineHeight: '21px',
    letterSpacing: '0.02em',
    alignItems: 'center',
  },
  title: {
    fontSize: '18px',
    fontWeight: '500',
    lineHeight: 'normal',
    letterSpacing: '0.02em',
    textTransform: 'capitalize',
  },
  headerButtonContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  closeIcon: {
    height: '16px',
    width: '16px',
    cursor: 'pointer',
  },
  textLabel: {
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '16px',
    paddingRight: '4px',
  },
  textValue: {
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '16px',
    letterSpacing: '-0.02em',
  },
  space: {
    width: '10px',
    display: 'inline-block',
  },
  textRow: {
    paddingBottom: '6px',
  },
  popupContext: {
    overflow: 'scroll',
    marginTop: '10px',
    maxHeight: '140px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  actionButton: {
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '16px',
    padding: '0',
    height: 'auto',
    minWidth: 'auto',
    marginRight: '10px',
    color: palette.sunburst,
  },
  expand: {
    color: palette.pictonBlue,
    margin: '0 11px',
  },
});

const RequestPopupContent = ({ onClose, stop }) => {
  const styles = useStyles();
  const { getModalRef } = useModalContext();

  const requestIcon = useMemo(() => {
    const { requests } = stop;
    const { type } = getStopRequestType(requests);
    return type;
  }, [stop]);

  const requestType = useMemo(() => getRequestType(stop), [stop]);

  const getPopupTitle = useCallback(() => {
    const addTitles = {
      [DRIVER_REQUEST_TYPE.COLLECTION]: ADD_COLLECTION,
      [DRIVER_REQUEST_TYPE.DELIVERY]: ADD_PARCEL,
    };
    const removeTitles = {
      [DRIVER_REQUEST_TYPE.COLLECTION]: REMOVE_COLLECTION,
      [DRIVER_REQUEST_TYPE.DELIVERY]: REMOVE_PARCEL,
      [DRIVER_REQUEST_TYPE.PICKUP]: PICKUP_UNSUITABLE,
    };

    switch (requestIcon) {
      case requestIconType.MIXED:
        return MIXED_REQUEST;
      case requestIconType.ADD:
        return addTitles[requestType];
      case requestIconType.REMOVE:
        return removeTitles[requestType];
      case requestIconType.DEFFER:
        return DEFERRED_REQUEST;
      default:
        return REQUEST;
    }
  }, [requestIcon, requestType]);

  const disableActions = useMemo(
    () =>
      (stop.stopTypeDescription === stopTypes.shop &&
        stop.requests.length > 1) ||
      requestIcon === requestIconType.MIXED,
    [stop, requestIcon]
  );

  const getRequestDetails = useCallback(() => {
    const textStyle = {
      label: styles.textLabel,
      value: styles.textValue,
      row: styles.textRow,
      spaceBetween: styles.space,
    };
    if (disableActions) {
      return (
        <GoToRouteDetailsWarning
          warningText={
            requestIcon === requestIconType.MIXED
              ? CONTAINS_MULTIPLE_REQUESTS
              : UNABLE_PROCEED_PICKUP_REQUEST
          }
        />
      );
    }
    if (requestType === DRIVER_REQUEST_TYPE.COLLECTION) {
      return <CollectionRequest stop={stop} textStyle={textStyle} />;
    }
    if (requestType === DRIVER_REQUEST_TYPE.PICKUP) {
      return <PickupRequest stop={stop} textStyle={textStyle} />;
    }
    return <ParcelRequest stop={stop} textStyle={textStyle} />;
  }, [stop, requestIcon, requestType, disableActions, styles]);

  const onExpandClick = useCallback(() => {
    onClose();

    const modalRef = getModalRef(modals.REQUEST);
    wrapPromise(
      modalRef.current.showModal({
        stop,
      })
    );
  }, [stop, onClose]);

  const isExpandVisible = useMemo(
    () => !disableActions && stop.requests.length > 1,
    [stop, disableActions]
  );

  return (
    <View className={styles.popupContainer}>
      <View className={styles.popupHeaderContainer}>
        <Text className={styles.title}>{getPopupTitle()}</Text>
        <View className={styles.headerButtonContainer}>
          {isExpandVisible && (
            <Button
              variant="text"
              className={clsx(styles.actionButton, styles.expand)}
              onClick={onExpandClick}
            >
              {EXPAND}
            </Button>
          )}
          <CloseIcon onClick={onClose} className={styles.closeIcon} />
        </View>
      </View>
      <View className={styles.popupContext}>{getRequestDetails()}</View>
      {!disableActions && <PopupRequestActions stop={stop} onClose={onClose} />}
    </View>
  );
};

export default memo(RequestPopupContent);
